/**
 * trapFocus(root)
 *
 * Constrains tab navigation inside `root`. Returns a release function.
 * Re-asserts the trap on every focusin (handles browser UI focus loss).
 */

const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex^="-"])',
].join(',');

/**
 * @param {HTMLElement} root
 * @returns {() => void} release
 */
export function trapFocus(root) {
  if (!(root instanceof HTMLElement)) {
    return () => {};
  }

  const previouslyFocused = /** @type {HTMLElement | null} */ (
    document.activeElement instanceof HTMLElement ? document.activeElement : null
  );

  const onKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'Tab') return;
    const items = visibleFocusable(root);
    if (items.length === 0) {
      event.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey) {
      if (active === first || !root.contains(active)) {
        last.focus();
        event.preventDefault();
      }
    } else if (active === last || !root.contains(active)) {
      first.focus();
      event.preventDefault();
    }
  };

  const onFocusin = (/** @type {FocusEvent} */ event) => {
    if (root.contains(/** @type {Node} */ (event.target))) return;
    const items = visibleFocusable(root);
    if (items.length > 0) items[0].focus();
  };

  document.addEventListener('keydown', onKeydown);
  document.addEventListener('focusin', onFocusin);

  return function release() {
    document.removeEventListener('keydown', onKeydown);
    document.removeEventListener('focusin', onFocusin);
    if (previouslyFocused && document.body.contains(previouslyFocused)) {
      previouslyFocused.focus({ preventScroll: true });
    }
  };
}

/**
 * @param {HTMLElement} root
 * @returns {HTMLElement[]}
 */
function visibleFocusable(root) {
  return /** @type {HTMLElement[]} */ (
    Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') {
        return false;
      }
      const rect = el.getBoundingClientRect();
      return rect.width > 0 || rect.height > 0;
    })
  );
}
